/**
 * Catálogo de tramas. Cada entrada recibe el contexto ya configurado con
 * color de tinta, opacidad y modo de fusión, y solo aporta geometría.
 *
 * Para añadir una: declara la clave en PatternKey, impleméntala aquí y
 * añádela a PATTERN_GROUPS. El tipo Record obliga a las tres cosas.
 */
import { clamp } from './color';
import type { BackgroundConfig, Color, Ctx, PatternApi, PatternFn, PatternKey, Rng } from './types';

export /* --- Tramas: cada entrada recibe el contexto ya configurado con color,
   opacidad y modo de fusión; solo dibuja geometría. --- */
function paintPattern(g: Ctx, W: number, H: number, ink: Color, cfg: BackgroundConfig, rnd: Rng) {
  if (cfg.pattern === 'none' || cfg.popacity === 0) return;
  const draw = TRAMAS[cfg.pattern];
  if (!draw) return;
  const s = cfg.pscale, [r, gg, b] = ink.rgb, rad = cfg.angle * Math.PI / 180;
  const R = Math.hypot(W, H) / 2;

  g.save();
  g.globalCompositeOperation = cfg.blend;
  g.globalAlpha = cfg.popacity;
  g.fillStyle = `rgb(${r},${gg},${b})`;
  g.strokeStyle = `rgb(${r},${gg},${b})`;
  g.lineWidth = Math.max(1, s * 0.055);
  g.lineCap = 'round';
  g.lineJoin = 'round';

  const api: PatternApi = {
    g, W, H, s, R, rad, rnd, ink,
    dot: (x, y, rr) => { if (rr <= 0) return; g.beginPath(); g.arc(x, y, rr, 0, 6.2832); g.fill(); },
    seg: (x1, y1, x2, y2) => { g.beginPath(); g.moveTo(x1, y1); g.lineTo(x2, y2); g.stroke(); },
    poly: (pts, fill) => {
      g.beginPath();
      pts.forEach((p, i) => i ? g.lineTo(p[0], p[1]) : g.moveTo(p[0], p[1]));
      g.closePath();
      if (fill) g.fill();
      else g.stroke();
    },
    /* dibuja en un sistema girado por el ángulo del control, cubriendo el lienzo */
    rot: fn => { g.save(); g.translate(W / 2, H / 2); g.rotate(rad); fn(R * 1.15); g.restore(); },
    /* recorre una retícula; row permite escalonar filas alternas */
    cells: (fn, step) => {
      const st = step || s;
      for (let y = 0, row = 0; y < H + st; y += st, row++)
        for (let x = 0; x < W + st; x += st) fn(x, y, row);
    },
    /* nº de elementos aleatorios proporcional al área, con techo de seguridad */
    count: (per, max) => Math.min(Math.round(W * H / (s * s) * per), max)
  };
  draw(api);
  g.restore();
}

export const TRAMAS: Record<Exclude<PatternKey, 'none'>, PatternFn> = {
  /* ---- puntos y semitonos ---- */
  dots: ({ s, dot, cells }) => cells((x, y, row) => dot(x + (row % 2) * s / 2, y, s * 0.11)),

  halftone: ({ W, H, s, rad, dot, cells }) => {
    const c = Math.cos(rad), sn = Math.sin(rad);
    const proj = [[0, 0], [W, 0], [0, H], [W, H]].map(([x, y]) => x! * c + y! * sn);
    const p0 = Math.min(...proj), p1 = Math.max(...proj);
    cells((x, y, row) => {
      const t = clamp((x * c + y * sn - p0) / (p1 - p0 || 1), 0, 1);
      dot(x + (row % 2) * s / 2, y, s * 0.44 * t * t);
    });
  },

  stipple: ({ W, H, s, rnd, dot, count }) => {
    const n = count(7, 9000);
    for (let i = 0; i < n; i++) dot(rnd() * W, rnd() * H, s * (0.02 + rnd() * 0.05));
  },

  terrazzo: ({ W, H, s, rnd, poly, count }) => {
    const n = count(0.55, 1400);
    for (let i = 0; i < n; i++) {
      const cx = rnd() * W, cy = rnd() * H, base = s * 0.09 * (0.5 + rnd()), rot0 = rnd() * 6.28;
      const pts: [number, number][] = [];
      for (let k = 0; k < 7; k++) {
        const a = rot0 + k / 7 * 6.2832, rr = base * (0.6 + rnd() * 0.8);
        pts.push([cx + Math.cos(a) * rr * 1.4, cy + Math.sin(a) * rr]);
      }
      poly(pts, true);
    }
  },

  confetti: ({ g, W, H, s, rnd, count }) => {
    const n = count(0.5, 1200);
    for (let i = 0; i < n; i++) {
      g.save(); g.translate(rnd() * W, rnd() * H); g.rotate(rnd() * 6.28);
      g.fillRect(-s * 0.05, -s * 0.16, s * 0.1, s * 0.32); g.restore();
    }
  },

  /* ---- líneas ---- */
  lines: ({ s, seg, rot }) => rot(R => { for (let x = -R; x < R; x += s) seg(x, -R, x, R); }),

  linesvar: ({ g, s, seg, rot }) => rot(R => {
    let i = 0;
    for (let x = -R; x < R; x += s, i++) {
      g.lineWidth = s * (0.03 + 0.1 * (0.5 + 0.5 * Math.sin(i * 0.55)));
      seg(x, -R, x, R);
    }
  }),

  dashes: ({ s, seg, rot }) => rot(R => {
    let row = 0;
    for (let y = -R; y < R; y += s * 0.6, row++)
      for (let x = -R + (row % 2) * s / 2; x < R; x += s) seg(x, y, x + s * 0.42, y);
  }),

  ticks: ({ s, seg, rot }) => rot(R => {
    for (let y = -R; y < R; y += s) {
      let i = 0;
      for (let x = -R; x < R; x += s * 0.25, i++) seg(x, y, x, y + s * (i % 4 === 0 ? 0.36 : 0.15));
    }
  }),

  zigzag: ({ g, s, rot }) => rot(R => {
    for (let y = -R; y < R; y += s * 0.7) {
      g.beginPath();
      let up = true;
      for (let x = -R; x < R; x += s * 0.5, up = !up) g.lineTo(x, y + (up ? 0 : s * 0.34));
      g.stroke();
    }
  }),

  waves: ({ g, W, H, s }) => {
    for (let y = 0; y < H + s; y += s) {
      g.beginPath();
      for (let x = 0; x <= W; x += 6) g.lineTo(x, y + Math.sin((x / s) * 1.2 + y * 0.02) * s * 0.28);
      g.stroke();
    }
  },

  sunburst: ({ g, W, H, s, R, rad }) => {
    const N = Math.max(8, Math.round(1400 / s)) * 2;
    for (let i = 0; i < N; i += 2) {
      const a0 = rad + i / N * 6.2832, a1 = rad + (i + 1) / N * 6.2832;
      g.beginPath(); g.moveTo(W / 2, H / 2); g.arc(W / 2, H / 2, R * 2, a0, a1); g.closePath(); g.fill();
    }
  },

  spiral: ({ g, W, H, s, R }) => {
    g.beginPath();
    for (let t = 0; ; t += 0.04) {
      const rr = t * s / 6.2832;
      if (rr > R * 1.5) break;
      g.lineTo(W / 2 + Math.cos(t) * rr, H / 2 + Math.sin(t) * rr);
    }
    g.stroke();
  },

  /* ---- rejillas ---- */
  grid: ({ W, H, s, seg }) => {
    for (let x = 0; x < W + s; x += s) seg(x, 0, x, H);
    for (let y = 0; y < H + s; y += s) seg(0, y, W, y);
  },

  blueprint: ({ g, W, H, s, seg }) => {
    const m = s / 4, base = g.lineWidth;
    if (m >= 7) {                       /* por debajo, la subdivisión se empasta */
      g.globalAlpha *= 0.45;
      for (let x = 0; x < W + m; x += m) seg(x, 0, x, H);
      for (let y = 0; y < H + m; y += m) seg(0, y, W, y);
      g.globalAlpha /= 0.45;
      g.lineWidth = base * 1.8;
    }
    for (let x = 0; x < W + s; x += s) seg(x, 0, x, H);
    for (let y = 0; y < H + s; y += s) seg(0, y, W, y);
  },

  cross: ({ s, seg, cells }) => cells((x, y) => {
    const k = s * 0.13;
    seg(x - k, y, x + k, y); seg(x, y - k, x, y + k);
  }),

  iso: ({ g, s, seg, rot }) => rot(R => {
    [0, 60, 120].forEach(deg => {
      g.save(); g.rotate(deg * Math.PI / 180);
      for (let x = -R; x < R; x += s) seg(x, -R, x, R);
      g.restore();
    });
  }),

  checker: ({ g, s, cells }) => cells((x, y, row) => {
    if ((Math.round(x / s) + row) % 2) g.fillRect(x, y, s, s);
  }),

  bricks: ({ s, seg, W, H }) => {
    const h = s * 0.5;
    let row = 0;
    for (let y = 0; y < H + h; y += h, row++) {
      seg(0, y, W, y);
      for (let x = (row % 2) * s / 2; x < W + s; x += s) seg(x, y, x, y + h);
    }
  },

  plaid: ({ g, W, H, s, ink }) => {
    const [r, gg, b] = ink.rgb;
    const bands: Array<[number, number]> = [[0.30, 0.85], [0.10, 0.40], [0.18, 0.60], [0.06, 0.30]];
    const run = (horiz: boolean) => {
      let p = 0, i = 0;
      while (p < (horiz ? W : H) + s) {
        const [w, a] = bands[i % bands.length]!;
        g.fillStyle = `rgba(${r},${gg},${b},${a})`;
        if (horiz) g.fillRect(p, 0, s * w, H);
        else g.fillRect(0, p, W, s * w);
        p += s * (w + 0.16); i++;
      }
    };
    run(true); run(false);
  },

  moire: ({ g, W, H, s, seg }) => {
    const grid = () => {
      for (let x = -W; x < W * 2; x += s) seg(x, -H, x, H * 2);
      for (let y = -H; y < H * 2; y += s) seg(-W, y, W * 2, y);
    };
    g.globalAlpha *= 0.6;
    grid();
    g.translate(W / 2, H / 2); g.rotate(6 * Math.PI / 180); g.translate(-W / 2, -H / 2);
    grid();
  },

  /* ---- formas ---- */
  tri: ({ g, s, rnd, cells }) => cells((x, y) => {
    if (rnd() > 0.45) return;
    g.beginPath(); g.moveTo(x, y); g.lineTo(x + s, y); g.lineTo(x, y + s); g.closePath(); g.fill();
  }),

  diamonds: ({ s, poly, cells }) => cells((x, y, row) => {
    const cx = x + (row % 2) * s / 2, k = s * 0.42;
    poly([[cx, y - k], [cx + k, y], [cx, y + k], [cx - k, y]], false);
  }),

  hex: ({ W, H, s, poly }) => {
    const r = s * 0.5, dx = Math.sqrt(3) * r, dy = 1.5 * r;
    for (let row = 0, y = 0; y < H + dy; row++, y += dy)
      for (let x = (row % 2) * dx / 2; x < W + dx; x += dx) {
        const pts: [number, number][] = [];
        for (let k = 0; k < 6; k++) {
          const a = (k * 60 - 30) * Math.PI / 180;
          pts.push([x + Math.cos(a) * r, y + Math.sin(a) * r]);
        }
        poly(pts, false);
      }
  },

  scales: ({ g, W, H, s }) => {
    for (let row = 0, y = 0; y < H + s; row++, y += s * 0.55)
      for (let x = (row % 2) * s / 2; x < W + s; x += s) {
        g.beginPath(); g.arc(x, y, s * 0.5, 0, Math.PI); g.stroke();
      }
  },

  truchet: ({ g, s, rnd, cells }) => cells((x, y) => {
    const r = s / 2;
    if (rnd() > 0.5) {
      g.beginPath(); g.arc(x, y, r, 0, Math.PI / 2); g.stroke();
      g.beginPath(); g.arc(x + s, y + s, r, Math.PI, Math.PI * 1.5); g.stroke();
    } else {
      g.beginPath(); g.arc(x + s, y, r, Math.PI / 2, Math.PI); g.stroke();
      g.beginPath(); g.arc(x, y + s, r, Math.PI * 1.5, Math.PI * 2); g.stroke();
    }
  }),

  rings: ({ g, W, H, s, R }) => {
    for (let rr = s * 0.5; rr < R * 1.5; rr += s * 0.6) {
      g.beginPath(); g.arc(W / 2, H / 2, rr, 0, 6.2832); g.stroke();
    }
  },

  topo: ({ g, W, H, s }) => {
    const cx = W * 0.5, cy = H * 0.55;
    for (let i = 1; i < 42; i++) {
      g.beginPath();
      for (let t = 0; t <= 64; t++) {
        const ang = (t / 64) * Math.PI * 2;
        const wob = 1 + Math.sin(ang * 3 + i * 0.55) * 0.12 + Math.cos(ang * 5 - i * 0.3) * 0.07;
        const rr = i * s * 0.42 * wob;
        g.lineTo(cx + Math.cos(ang) * rr * 1.35, cy + Math.sin(ang) * rr);
      }
      g.closePath(); g.stroke();
    }
  }
};

export const PATTERN_GROUPS: [string, [PatternKey, string][]][] = [
  ['Puntos y semitonos', [
    ['dots', 'Puntos'], ['halftone', 'Semitono degradado'], ['stipple', 'Punteado aleatorio'],
    ['terrazzo', 'Terrazo'], ['confetti', 'Confeti']
  ]],
  ['Líneas', [
    ['lines', 'Rayas diagonales'], ['linesvar', 'Rayas de grosor variable'], ['dashes', 'Guiones'],
    ['ticks', 'Marcas de regla'], ['zigzag', 'Zigzag'], ['waves', 'Ondas'],
    ['sunburst', 'Rayos radiales'], ['spiral', 'Espiral']
  ]],
  ['Rejillas', [
    ['grid', 'Rejilla'], ['blueprint', 'Rejilla técnica'], ['cross', 'Cruces'], ['iso', 'Isométrica'],
    ['checker', 'Damero'], ['bricks', 'Ladrillos'], ['plaid', 'Cuadros'], ['moire', 'Muaré']
  ]],
  ['Formas', [
    ['tri', 'Triángulos'], ['diamonds', 'Rombos'], ['hex', 'Panal hexagonal'], ['scales', 'Escamas'],
    ['truchet', 'Truchet'], ['rings', 'Anillos'], ['topo', 'Topografía']
  ]]
];
